import { fontHeading4 } from 'libs/growth-platform-brand-system-v2/src/styles/typographyNew';
import { AnimChangeChildren } from 'libs/growth-platform-brand-system-v2/src/utils/AnimChangeChildren';
import { classNames } from 'libs/growth-platform-brand-system-v2/src/utils/style';
import React from 'react';
import {
  LabelsPricingPageRecord,
  NbTagRecord,
  SiteLocale,
  TooltipRecord,
} from '../../../../../types/dato';
import PriceBox from '../PriceBox';
import { BreakdownTooltips } from '../PriceBox/BreakdownTooltips';
import { checkDiscountIsCheaper } from '../scripts/checkDiscountIsCheaper';
import { filterPlural } from '../scripts/filterPlural';
import {
  OfferProps,
  PricesRangeWithTotal,
  PricingRangeWithDiscountAndTotalProps,
} from '../types';

const PriceContent = ({
  price,
  discount,
  locale,
  tag,
}: {
  price?: number;
  discount?: number;
  locale: SiteLocale;
  tag?: NbTagRecord;
}) => {
  if (!discount) {
    return <PriceBox.Price price={price} locale={locale} tag={tag} />;
  }
  if (discount) {
    return (
      <PriceBox.PriceDiscount
        price={price}
        discount={discount}
        locale={locale}
        tag={tag}
      />
    );
  }
};

const BaseContent = ({
  price,
  discount,
  locale,
  label,
  tooltip,
}: {
  price?: number;
  locale: SiteLocale;
  label?: string;
  discount?: number;
  tooltip?: TooltipRecord | null;
}) => {
  if (!discount) {
    return (
      <PriceBox.Base
        price={price}
        locale={locale}
        label={label}
        tooltip={tooltip}
      />
    );
  }
  if (discount) {
    return (
      <PriceBox.BaseDiscount
        price={price}
        locale={locale}
        label={label}
        discount={discount}
        tooltip={tooltip}
      />
    );
  }
};

const TotalContent = ({
  locale,
  labels,
  priceRange,
  discount,
  nbEmployee,
  offer,
}: {
  locale: SiteLocale;
  labels?: LabelsPricingPageRecord | null;
  priceRange?: PricingRangeWithDiscountAndTotalProps;
  discount?: number;
  nbEmployee: number;
  offer: OfferProps;
}) => {
  if (!discount) {
    return (
      <PriceBox.Total
        locale={locale}
        totalPriceLabel={
          filterPlural(
            nbEmployee,
            labels?.totalPriceLabel as string,
            labels?.totalPriceLabelPlural as string,
          ) || ''
        }
        price={priceRange?.total}
        nbEmployee={nbEmployee}
        breakdownTooltip={
          <BreakdownTooltips
            labels={labels}
            pricesRange={priceRange}
            locale={locale}
            nbEmployee={nbEmployee}
            offer={offer}
          />
        }
      />
    );
  }
  if (discount) {
    return (
      <PriceBox.TotalDiscount
        locale={locale}
        totalPriceLabel={
          filterPlural(
            nbEmployee,
            labels?.totalPriceLabel as string,
            labels?.totalPriceLabelPlural as string,
          ) || ''
        }
        price={priceRange?.total}
        discount={discount}
        nbEmployee={nbEmployee}
        breakdownTooltip={
          <BreakdownTooltips
            labels={labels}
            pricesRange={priceRange}
            locale={locale}
            nbEmployee={nbEmployee}
            offer={offer}
          />
        }
      />
    );
  }
};

const PriceSection = ({
  pricesRange,
  discount,
  nbEmployee,
  locale,
  labels,
  offer,
}: {
  pricesRange?: PricingRangeWithDiscountAndTotalProps | null;
  discount?: PricesRangeWithTotal | null;
  locale: SiteLocale;
  labels?: LabelsPricingPageRecord | null;
  nbEmployee: number;
  offer: OfferProps;
}) => {
  const animationSettings = {
    exit: { opacity: 0, y: -30 },
    exitTransition: { duration: 0.3 },
    initial: { opacity: 0, y: 20 },
    animateTransition: { duration: 0.3, delay: 0.3 },
  };

  return !pricesRange ? (
    <div className={classNames(fontHeading4)}>{labels?.onDemandTitleLabel}</div>
  ) : (
    <PriceBox>
      <div className={classNames('relative flex flex-col gap-[16px]')}>
        <AnimChangeChildren
          {...animationSettings}
          className={classNames('-mb-[10px]')}
        >
          <PriceContent
            price={pricesRange.price}
            discount={
              checkDiscountIsCheaper(pricesRange.price, discount?.price)
                ? discount?.price
                : undefined
            }
            locale={locale}
            tag={offer.tag}
          />
        </AnimChangeChildren>
        <div>
          <div className={classNames('font-medium')}>{labels?.priceLabel}</div>
          <div className={classNames('overflow-hidden text-[#55687D]')}>
            <AnimChangeChildren {...animationSettings}>
              <BaseContent
                locale={locale}
                label={labels?.baseLabel as string}
                price={pricesRange.base}
                tooltip={labels?.baseTooltip}
                discount={
                  checkDiscountIsCheaper(pricesRange.base, discount?.base)
                    ? discount?.base
                    : undefined
                }
              />
            </AnimChangeChildren>
          </div>
        </div>
      </div>
      <div className={classNames('flex')}>
        <TotalContent
          locale={locale}
          labels={labels}
          priceRange={pricesRange}
          discount={
            checkDiscountIsCheaper(pricesRange.total, discount?.total)
              ? discount?.total
              : undefined
          }
          nbEmployee={nbEmployee}
          offer={offer}
        />
      </div>
    </PriceBox>
  );
};

export default PriceSection;
